import React from 'react';
import { useSearchParams } from 'react-router-dom';
import ProcessingTab from '../Admin/ProcessingTab';
import RateCardsTab from '../Admin/RateCardsTab';
import ChannelMappingTab from '../Admin/ChannelMappingTab';
import FinancialsTab from '../Admin/FinancialsTab';
import CorrectionsTab from './Corrections/CorrectionsTab';
import PipelineTab from '../Admin/Pipeline/PipelineTab';
import usePipelineController from '../Admin/Pipeline/usePipelineController';
import { PipelineBadge } from '../Admin/Pipeline/pipelineUtils';
import { Tabs, DashboardFrame, Button } from '../ui';
import { usePermissions } from '../../hooks/usePermissions';

type TabKey = 'pipeline' | 'processing' | 'rate-cards' | 'channel-mapping' | 'financials' | 'corrections' | 'direct-sold-ads';

const TAB_KEYS: TabKey[] = ['pipeline', 'processing', 'rate-cards', 'channel-mapping', 'financials', 'corrections', 'direct-sold-ads'];

const DOT_COLOR: Record<Exclude<PipelineBadge, null>, string> = {
  running: 'var(--success)',
  attention: 'var(--accent-2)',
  failed: 'var(--danger)',
};

const DOT_TITLE: Record<Exclude<PipelineBadge, null>, string> = {
  running: 'Pipeline running',
  attention: 'Pipeline needs your action',
  failed: 'A pipeline step failed',
};

/** Status dot on the Pipeline tab label — visible from every tab. */
const TabDot: React.FC<{ badge: PipelineBadge }> = ({ badge }) => {
  if (!badge) return null;
  return (
    <span
      title={DOT_TITLE[badge]}
      style={{
        display: 'inline-block',
        width: 7,
        height: 7,
        borderRadius: 'var(--radius-pill)',
        background: DOT_COLOR[badge],
        marginLeft: 7,
        verticalAlign: 'middle',
        animation: badge === 'running' ? 'cgPulse 1.5s ease-in-out infinite' : undefined,
      }}
    />
  );
};

/** Ad Intelligence moved to its own dashboard. Short-lived redirect stub. */
const AdIntelligenceMoved: React.FC = () => (
  <div style={{ padding: 32, textAlign: 'center', color: 'var(--fg-2)' }}>
    <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8, color: 'var(--fg-1)' }}>
      Ad Intelligence has moved
    </div>
    <p style={{ maxWidth: 420, margin: '0 auto 16px', fontSize: 14 }}>
      It now lives in the new <strong>Channel Intelligence</strong> dashboard, alongside
      YouTube performance, audience, and a chat assistant.
    </p>
    <Button onClick={() => { window.location.href = '/dashboards/channel-intelligence'; }}>
      Open Channel Intelligence →
    </Button>
  </div>
);

const FinancialsDashboard: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Pipeline state lives here (not in the tab) so the tab-label dot stays live
  // while the user is on other tabs. The controller polls 10s while running.
  const pipelineController = usePipelineController();
  const { has } = usePermissions();

  const tabParam = searchParams.get('tab') as TabKey | null;
  const activeTab: TabKey = tabParam && TAB_KEYS.includes(tabParam) ? tabParam : 'pipeline';

  const setActiveTab = (tab: TabKey) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      return next;
    });
  };

  /** Corrections deep links: `cmonth` (list filter) + `correction` (open one). `month` stays the pipeline's. */
  const openCorrections = (opts: { month?: string | null; id?: string | null } = {}) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', 'corrections');
      if (opts.month) next.set('cmonth', opts.month);
      else next.delete('cmonth');
      if (opts.id) next.set('correction', opts.id);
      else next.delete('correction');
      return next;
    });
  };

  const selectCorrection = (id: string | null) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (id) next.set('correction', id);
      else next.delete('correction');
      return next;
    });
  };

  const renderTab = () => {
    switch (activeTab) {
      case 'pipeline':
        return (
          <PipelineTab
            controller={pipelineController}
            onNavigateTab={setActiveTab}
            onViewCorrections={(month) => openCorrections({ month })}
          />
        );
      case 'processing':      return <ProcessingTab />;
      case 'rate-cards':      return <RateCardsTab />;
      case 'channel-mapping': return <ChannelMappingTab />;
      case 'financials':      return <FinancialsTab onViewCorrections={(month) => openCorrections({ month })} />;
      case 'corrections':
        return (
          <CorrectionsTab
            canCorrect={has('financials.corrections')}
            selectedId={searchParams.get('correction')}
            onSelect={selectCorrection}
            initialMonth={searchParams.get('cmonth')}
          />
        );
      case 'direct-sold-ads': return <AdIntelligenceMoved />;
      default:                return null;
    }
  };

  return (
    <DashboardFrame
      eyebrow="Financials · Revenue ledger"
      title="Financials"
      tabs={
        <Tabs<TabKey>
          value={activeTab}
          onChange={setActiveTab}
          variant="underlined"
          options={[
            {
              value: 'pipeline',
              label: (
                <>
                  Pipeline
                  <TabDot badge={pipelineController.phase.badge} />
                </>
              ),
            },
            { value: 'processing',      label: 'DS Processing' },
            { value: 'rate-cards',      label: 'YT Payout Rates' },
            { value: 'channel-mapping', label: 'Channel Mapping' },
            { value: 'financials',      label: 'Earnings' },
            { value: 'corrections',     label: 'Corrections' },
            { value: 'direct-sold-ads', label: 'Ad Intelligence ↗' },
          ]}
        />
      }
    >
      {renderTab()}
    </DashboardFrame>
  );
};

export default FinancialsDashboard;
